import {
	Button,
	ButtonUtility,
	FormFieldControl,
	Input,
	Modal,
	ModalOverlay,
	ToastCustom,
} from "@components";
import { AlertCircle, XClose } from "@untitledui/icons";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { CarIcon } from "@/components/ui/icons/CarIcon";
import { mapBodyTypeToLabel } from "../handlers";
import { useCreateVehicleAssignment } from "../query";
import { getVehicleActiveAssignment } from "../services";
import type { VehicleAllListItem } from "../types";
import { TrailerPlateComboBox } from "./trailer-plate-combobox";

type AssignTrailerModalProps = {
	isOpen: boolean;
	onClose: () => void;
	vehicleId: number;
	vehiclePlate: string;
	assignedTrailerIds?: number[];
	onSuccess?: () => void;
};

type ReplaceConfirmViewProps = {
	trailerPlate: string;
	linkedVehiclePlate: string;
	vehiclePlate: string;
	isSubmitting: boolean;
	onClose: () => void;
	onConfirm: () => void;
};

const ReplaceConfirmView = ({
	trailerPlate,
	linkedVehiclePlate,
	vehiclePlate,
	isSubmitting,
	onClose,
	onConfirm,
}: ReplaceConfirmViewProps) => (
	<>
		<ButtonUtility
			className="absolute top-5 right-5"
			color="tertiary"
			icon={<XClose className="size-5 text-quaternary" />}
			onClick={onClose}
			tooltip="Fechar"
		/>

		<div className="pointer-events-none relative mb-4 flex items-center justify-center">
			<FeaturedIcon color="warning" icon={AlertCircle} size="lg" />
			{[20, 28, 36, 44, 52].map((size) => (
				<div
					className="absolute rounded-full border border-gray-200"
					key={size}
					style={{
						height: `${size * 4}px`,
						width: `${size * 4}px`,
						opacity: (70 - size / 2) / 100,
					}}
				/>
			))}
		</div>

		<h1 className="mb-2 font-semibold text-base text-gray-900">
			Substituir atribuição atual?
		</h1>
		<p className="mb-6 text-gray-600 text-sm">
			O reboque <strong>{trailerPlate}</strong> já está atribuído ao veículo{" "}
			<strong>{linkedVehiclePlate}</strong>. Ao confirmar, ele será atribuído ao
			veículo <strong>{vehiclePlate}</strong>.
		</p>

		<div className="flex flex-row gap-3">
			<Button
				className="flex-1"
				onClick={onClose}
				size="lg"
				type="button"
				variant="secondary"
			>
				Cancelar
			</Button>
			<Button
				className="flex-1"
				disabled={isSubmitting}
				onClick={onConfirm}
				size="lg"
				type="button"
			>
				Confirmar Atribuição
			</Button>
		</div>
	</>
);

type AssignTrailerFormProps = {
	vehicleId: number;
	isOpen: boolean;
	selectedVehicle: VehicleAllListItem | null;
	onSelectVehicle: (vehicle: VehicleAllListItem | null) => void;
	isBusy: boolean;
	onClose: () => void;
	onConfirm: () => void;
	assignedTrailerIds: number[];
};

const AssignTrailerForm = ({
	vehicleId,
	isOpen,
	selectedVehicle,
	onSelectVehicle,
	isBusy,
	onClose,
	onConfirm,
	assignedTrailerIds,
}: AssignTrailerFormProps) => (
	<div className="flex h-full min-h-0 flex-col">
		<div className="flex flex-row items-center gap-4 border-b border-b-border-secondary border-solid bg-bg-primary p-6 pb-5">
			<div className="flex items-center justify-center rounded-[10px] border border-primary border-solid p-3">
				<CarIcon className="size-6 text-secondary" />
			</div>
			<div className="flex flex-col gap-0.5 text-left">
				<h1 className="font-semibold text-base text-gray-900">
					Atribuir Reboque ou Semirreboque
				</h1>
				<p className="text-gray-600 text-sm">
					Digite a placa de um reboque ou semirreboque para atribuir a este
					veículo
				</p>
			</div>
		</div>

		<ButtonUtility
			className="absolute top-7 right-5 z-10"
			color="tertiary"
			icon={<XClose className="size-6 text-quaternary" />}
			onClick={onClose}
			tooltip="Fechar"
		/>

		<div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto px-6 pt-5 pb-8">
			<FormFieldControl htmlFor="trailerPlate" label="Placa">
				{selectedVehicle ? (
					<div className="flex items-center gap-2">
						<Input disabled id="trailerPlate" value={selectedVehicle.plate} />
						<ButtonUtility
							color="tertiary"
							icon={<XClose className="size-5 text-quaternary" />}
							onClick={() => onSelectVehicle(null)}
							tooltip="Trocar placa"
						/>
					</div>
				) : (
					<TrailerPlateComboBox
						assignedTrailerIds={assignedTrailerIds}
						excludeVehicleId={vehicleId}
						isOpen={isOpen}
						onSelect={onSelectVehicle}
						selectedVehicle={selectedVehicle}
					/>
				)}
			</FormFieldControl>

			<FormFieldControl htmlFor="trailerBodyType" label="Tipo de Veículo">
				<Input
					disabled
					id="trailerBodyType"
					value={
						selectedVehicle
							? mapBodyTypeToLabel(Number(selectedVehicle.bodyType))
							: ""
					}
				/>
			</FormFieldControl>

			<FormFieldControl htmlFor="trailerModel" label="Modelo">
				<Input
					disabled
					id="trailerModel"
					value={
						selectedVehicle ? selectedVehicle.model || "Não informado" : ""
					}
				/>
			</FormFieldControl>

			<FormFieldControl htmlFor="trailerBrand" label="Marca">
				<Input
					disabled
					id="trailerBrand"
					value={
						selectedVehicle ? selectedVehicle.brand || "Não informado" : ""
					}
				/>
			</FormFieldControl>

			<FormFieldControl htmlFor="trailerDescription" label="Descrição">
				<Input
					disabled
					id="trailerDescription"
					value={
						selectedVehicle
							? selectedVehicle.description || "Não informado"
							: ""
					}
				/>
			</FormFieldControl>

			<FormFieldControl htmlFor="trailerModelYear" label="Ano do modelo">
				<Input
					disabled
					id="trailerModelYear"
					value={
						selectedVehicle
							? String(selectedVehicle.modelYear || "Não informado")
							: ""
					}
				/>
			</FormFieldControl>

			<FormFieldControl
				htmlFor="trailerManufactureYear"
				label="Ano de fabricação"
			>
				<Input
					disabled
					id="trailerManufactureYear"
					value={
						selectedVehicle
							? String(selectedVehicle.manufactureYear || "Não informado")
							: ""
					}
				/>
			</FormFieldControl>
		</div>

		<div className="flex flex-row justify-end gap-3 border-t border-t-border-secondary border-solid bg-bg-primary p-6">
			<Button
				className="flex-1"
				disabled={isBusy}
				onClick={onClose}
				size="lg"
				type="button"
				variant="secondary"
			>
				Cancelar
			</Button>
			<Button
				className="flex-1"
				disabled={!selectedVehicle || isBusy}
				onClick={onConfirm}
				size="lg"
				type="button"
			>
				Confirmar atribuição
			</Button>
		</div>
	</div>
);

export const AssignTrailerModal = ({
	isOpen,
	onClose,
	vehicleId,
	vehiclePlate,
	assignedTrailerIds = [],
	onSuccess,
}: AssignTrailerModalProps) => {
	const [selectedVehicle, setSelectedVehicle] =
		useState<VehicleAllListItem | null>(null);
	const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false);
	const [linkedVehiclePlate, setLinkedVehiclePlate] = useState<string | null>(
		null
	);
	const [isCheckingAssignment, setIsCheckingAssignment] = useState(false);
	const createAssignment = useCreateVehicleAssignment();

	const handleClose = () => {
		setSelectedVehicle(null);
		setIsReplaceConfirmOpen(false);
		setLinkedVehiclePlate(null);
		onClose();
	};

	const closeReplaceConfirm = () => {
		setIsReplaceConfirmOpen(false);
		setLinkedVehiclePlate(null);
	};

	const submitAssignment = async () => {
		if (!selectedVehicle) {
			return;
		}

		try {
			await createAssignment.mutateAsync({
				vehicleId,
				trailerVehicleId: selectedVehicle.id,
			});
			toast(
				ToastCustom({
					title: "Atribuição realizada com sucesso",
					variant: "success",
				})
			);
			onSuccess?.();
			handleClose();
		} catch (error) {
			const detail = axios.isAxiosError(error)
				? error.response?.data?.error?.detail
				: undefined;

			toast(
				ToastCustom({
					title: detail ?? "Não foi possível atribuir o veículo",
					variant: "error",
				})
			);
		}
	};

	const handleConfirm = async () => {
		if (!selectedVehicle) {
			return;
		}

		setIsCheckingAssignment(true);
		try {
			const activeAssignment = await getVehicleActiveAssignment(
				String(selectedVehicle.id)
			);

			if (
				activeAssignment.hasActiveAssignment &&
				activeAssignment.linkedVehicle?.id !== vehicleId
			) {
				setLinkedVehiclePlate(activeAssignment.linkedVehicle?.plate ?? null);
				setIsReplaceConfirmOpen(true);
				return;
			}

			await submitAssignment();
		} catch {
			toast(
				ToastCustom({
					title: "Não foi possível verificar a disponibilidade do reboque",
					variant: "error",
				})
			);
		} finally {
			setIsCheckingAssignment(false);
		}
	};

	const handleReplaceCurrentAssignment = async () => {
		closeReplaceConfirm();
		await submitAssignment();
	};

	const isBusy = createAssignment.isPending || isCheckingAssignment;

	return (
		<ModalOverlay
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleClose();
				}
			}}
		>
			<Modal
				className={
					isReplaceConfirmOpen
						? "relative mx-auto h-auto max-w-[400px]! gap-0 overflow-hidden rounded-2xl bg-bg-primary p-6"
						: "relative mx-auto max-h-[calc(100vh-8rem)] max-w-[640px]! gap-0 overflow-hidden rounded-2xl bg-bg-primary p-0"
				}
			>
				{isReplaceConfirmOpen ? (
					<ReplaceConfirmView
						isSubmitting={createAssignment.isPending}
						linkedVehiclePlate={linkedVehiclePlate ?? "outro veículo"}
						onClose={closeReplaceConfirm}
						onConfirm={handleReplaceCurrentAssignment}
						trailerPlate={selectedVehicle?.plate ?? ""}
						vehiclePlate={vehiclePlate}
					/>
				) : (
					<AssignTrailerForm
						assignedTrailerIds={assignedTrailerIds}
						isBusy={isBusy}
						isOpen={isOpen}
						onClose={handleClose}
						onConfirm={handleConfirm}
						onSelectVehicle={setSelectedVehicle}
						selectedVehicle={selectedVehicle}
						vehicleId={vehicleId}
					/>
				)}
			</Modal>
		</ModalOverlay>
	);
};
